import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';

const UserPreferencesContext = createContext();

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};

export const UserPreferencesProvider = ({ children }) => {
  console.log('🎯 UserPreferencesProvider initialized');
  const [user, setUser] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [loading, setLoading] = useState(false); // Start with false for instant loading
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [hasHandledRedirect, setHasHandledRedirect] = useState(false);

  useEffect(() => {
    console.log('🎯 useEffect running in UserPreferencesProvider');
    // Get initial session with timeout
    const getInitialSession = async () => {
      try {
        console.log('🚀 Getting initial session...');
        
        // Add timeout to the Supabase call
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session timeout')), 3000)
        );
        
        const { data: { session } } = await Promise.race([sessionPromise, timeoutPromise]);
        console.log('🔍 Initial session result - User exists:', !!session?.user);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          console.log('👤 Initial session has user, loading preferences...');
          await loadUserPreferences(session.user.id);
        } else {
          console.log('👤 No initial user, checking guest preferences...');
          // Check for guest preferences in localStorage
          const guestPrefs = localStorage.getItem('guestPreferences');
          if (guestPrefs) {
            const parsedPrefs = JSON.parse(guestPrefs);
            setPreferences(parsedPrefs);
            setIsOnboarded(!!parsedPrefs.name);
            console.log('✅ Initial guest preferences loaded');
          } else {
            console.log('✅ No initial guest preferences');
          }
          console.log('✅ Initial session completed (no user)');
        }
      } catch (error) {
        console.error('❌ Error getting initial session:', error);
        console.log('🔄 Falling back to auth state listener only');
        
        // Since initial session failed, let's check localStorage immediately
        const guestPrefs = localStorage.getItem('guestPreferences');
        if (guestPrefs) {
          const parsedPrefs = JSON.parse(guestPrefs);
          setPreferences(parsedPrefs);
          setIsOnboarded(!!parsedPrefs.name);
          console.log('✅ Loaded guest preferences as fallback');
        }
        // If no guest prefs, let the timeout handle setting loading to false
      }
    };

    // Try initial session but don't wait for it
    getInitialSession().catch(err => console.log('Initial session failed, relying on auth listener'));

    // No longer need fallback timeout since we start with loading=false

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Auth state change:', event, 'User exists:', !!session?.user);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          console.log('👤 User is authenticated, loading preferences...');
          await loadUserPreferences(session.user.id);
        } else {
          console.log('👤 No user, checking guest preferences...');
          // User logged out, fall back to guest mode
          const guestPrefs = localStorage.getItem('guestPreferences');
          if (guestPrefs) {
            const parsedPrefs = JSON.parse(guestPrefs);
            setPreferences(parsedPrefs);
            setIsOnboarded(!!parsedPrefs.name);
            console.log('✅ Guest preferences loaded');
          } else {
            setPreferences(null);
            setIsOnboarded(false);
            console.log('✅ No guest preferences found');
          }
          // Reset signing out flag after sign out is complete
          if (event === 'SIGNED_OUT') {
            setIsSigningOut(false);
            setHasHandledRedirect(false); // Reset for next sign-in
          }
          console.log('✅ Signed out user state set');
        }
        // Note: Don't set loading to false here for signed in users, 
        // loadUserPreferences will handle it
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const loadUserPreferences = async (userId) => {
    console.log('🔄 loadUserPreferences called for userId:', userId);
    try {
      console.log('🔍 Querying user_prefs table...');
      const { data, error } = await supabase
        .from('user_prefs')
        .select('*')
        .eq('user_id', userId);

      if (error) {
        console.error('❌ Supabase error loading preferences:', error);
        // Still continue - user might not have preferences yet
        setPreferences(null);
        setIsOnboarded(false);
        console.log('✅ Set loading to false after error');
        setLoading(false);
        return;
      }

      console.log('✅ Query successful, data:', data);
      if (data && data.length > 0) {
        console.log('👤 User has preferences, setting up...');
        setPreferences(data[0]); // Take the first record
        setIsOnboarded(true);
        console.log('✅ User preferences set, onboarded=true');
      } else {
        console.log('👤 No user preferences found - user not onboarded');
        setPreferences(null);
        setIsOnboarded(false);
      }
      console.log('✅ User preferences loading completed');
    } catch (error) {
      console.error('❌ Exception in loadUserPreferences:', error);
      setPreferences(null);
      setIsOnboarded(false);
      console.log('✅ Error handling completed');
    }
  };

  const updatePreferences = async (updates) => {
    console.log('🔄 updatePreferences called with updates:', updates);
    console.log('🔄 Current user:', !!user);
    
    try {
      if (user) {
        // Authenticated user - save to Supabase
        console.log('🔄 Saving to Supabase for user:', user.id);
        
        // Use the upsert_user_prefs function for proper data handling
        const supabasePromise = supabase.rpc('upsert_user_prefs', {
          p_name: updates.name,
          p_layer_shift: updates.layer_shift,
          p_vote_shift: updates.vote_shift,
          p_home_city: updates.home_city,
          p_home_koppen: updates.home_koppen,
          p_home_t30: updates.home_T30,
          p_prev_tile: updates.prev_tile,
          p_prev_city: updates.prev_city,
          p_arrival_month: updates.arrival_month,
          p_default_mode: updates.default_mode,
          p_cycling_speed: updates.cycling_speed
        });
          
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Supabase upsert timeout')), 5000)
        );
        
        let data, error;
        try {
          const result = await Promise.race([supabasePromise, timeoutPromise]);
          data = result.data;
          error = result.error;
        } catch (timeoutError) {
          console.error('🔄 Supabase timeout, using localStorage fallback');
          error = timeoutError;
        }

        if (error) {
          console.error('🔄 Supabase error, falling back to localStorage:', error);
          // Fall back to localStorage if Supabase fails
          const fallbackPrefs = {
            ...preferences,
            ...updates,
            user_id: user.id,
            updated_at: new Date().toISOString()
          };
          localStorage.setItem('guestPreferences', JSON.stringify(fallbackPrefs));
          setPreferences(fallbackPrefs);
          setIsOnboarded(true);
          console.log('🔄 Fallback to localStorage successful');
          return fallbackPrefs;
        }

        console.log('🔄 Supabase save successful:', data);
        setPreferences(data);
        setIsOnboarded(true);
        console.log('🔄 State updated - isOnboarded: true');
        return data;
      } else {
        // Guest user - save to localStorage
        const guestPrefs = {
          ...preferences,
          ...updates,
          updated_at: new Date().toISOString()
        };
        
        localStorage.setItem('guestPreferences', JSON.stringify(guestPrefs));
        setPreferences(guestPrefs);
        setIsOnboarded(!!guestPrefs.name);
        return guestPrefs;
      }
    } catch (error) {
      console.error('Failed to update preferences:', error);
      throw error;
    }
  };

  const completeOnboarding = async (onboardingData) => {
    console.log('🎯 completeOnboarding called with:', onboardingData);
    
    // Base preferences data
    const prefsData = {
      name: onboardingData.name,
      home_city: onboardingData.location,
      layer_shift: getLayerShiftFromTemperatureBias(onboardingData.temperatureBias),
      default_mode: 'walk',
      arrival_month: onboardingData.isNewcomer ? onboardingData.arrivalMonth : null,
      prev_city: onboardingData.isNewcomer ? onboardingData.originCity : null,
      created_at: new Date().toISOString()
    };

    console.log('🎯 Prepared prefsData:', prefsData);
    
    // If user is a newcomer with origin city, get Köppen climate data
    if (onboardingData.isNewcomer && onboardingData.originCity && onboardingData.arrivalMonth && user) {
      console.log('🌍 Starting Köppen API section for newcomer');
      console.log('🌍 Origin city:', onboardingData.originCity);
      console.log('🌍 Arrival month:', onboardingData.arrivalMonth);
      console.log('🌍 User exists:', !!user);
      
    try {
      console.log('🌍 Calling Köppen edge function...');
      const { data: koppenData, error: koppenError } = await supabase.functions.invoke('koppen-climate', {
        body: {
          latitude: onboardingData.originCoordinates.latitude,
          longitude: onboardingData.originCoordinates.longitude
        }
      });

      if (koppenError) {
        console.error('🌍 Köppen edge function error:', koppenError);
      } else if (koppenData?.success) {
        console.log('🌍 Köppen data received:', koppenData);
        prefsData.home_koppen = koppenData.koppen_zone;
        prefsData.prev_tile = koppenData.koppen_zone;
        console.log('🌍 Updated prefsData with Köppen data');
      } else {
        console.log('🌍 Köppen API returned no data');
      }
    } catch (error) {
      console.error('🌍 Error calling Köppen edge function:', error);
    }
    } else {
      console.log('🌍 Skipping Köppen API section:', {
        isNewcomer: onboardingData.isNewcomer,
        hasOriginCity: !!onboardingData.originCity,
        hasArrivalMonth: !!onboardingData.arrivalMonth,
        hasUser: !!user
      });
    }
    
    console.log('🎯 Proceeding to updatePreferences...');
    
    console.log('🎯 Calling updatePreferences...');
    
    try {
      const result = await updatePreferences(prefsData);
      console.log('🎯 updatePreferences completed successfully:', result);
      return result;
    } catch (error) {
      console.error('🎯 updatePreferences failed:', error);
      throw error;
    }
  };

  // Helper function to convert temperature bias to layer_shift
  const getLayerShiftFromTemperatureBias = (bias) => {
    switch (bias) {
      case 'cold': return 1;  // Runs cold, add layer
      case 'warm': return -1; // Runs warm, remove layer
      case 'neutral': 
      default: return 0;
    }
  };

  const signOut = async () => {
    console.log('UserPreferencesContext signOut called');
    
    // Set signing out flag to prevent unwanted redirects
    setIsSigningOut(true);
    
    // Immediately clear local state to prevent any UI confusion
    localStorage.removeItem('guestPreferences');
    setPreferences(null);
    setIsOnboarded(false);
    setUser(null);
    console.log('Local state cleared immediately');
    
    try {
      // Add timeout to Supabase sign out call
      const signOutPromise = supabase.auth.signOut();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Sign out timeout')), 3000)
      );
      
      const { error } = await Promise.race([signOutPromise, timeoutPromise]);
      if (error) {
        console.error('Supabase sign out error:', error);
      }
      console.log('Supabase sign out completed');
    } catch (error) {
      console.error('Sign out error:', error);
    }
    
    // No automatic redirect - let pages handle their own routing
  };

  const value = {
    user,
    preferences,
    isOnboarded,
    loading,
    updatePreferences,
    completeOnboarding,
    loadUserPreferences,
    signOut,
    // Helper to determine if user should see personalized vs basic app
    shouldShowPersonalizedApp: () => user && isOnboarded && preferences?.name,
    // Helper to check if user is authenticated
    isAuthenticated: () => !!user
  };

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export default UserPreferencesContext;